import { Chain, Currency } from "@paywithglide/glide-js";

export type MultichainChainType = "evm" | "solana" | "bitcoin" | "tron";

interface MultichainEligibilityOptions {
  destinationIsTestnet?: boolean;
  chainIds?: string[];
  excludeChainIds?: string[];
  excludeBitcoin?: boolean;
}

export function isAllowedMultichainChain(
  chain: Chain,
  options: MultichainEligibilityOptions,
): boolean {
  const { destinationIsTestnet, chainIds, excludeChainIds, excludeBitcoin } =
    options;

  if (
    destinationIsTestnet !== undefined &&
    chain.isTestnet !== destinationIsTestnet
  ) {
    return false;
  }

  if (chainIds?.length && !chainIds.includes(chain.id)) {
    return false;
  }

  if (excludeChainIds?.includes(chain.id)) {
    return false;
  }

  if (excludeBitcoin && chain.id.startsWith("bip122:")) {
    return false;
  }

  return true;
}

export function getAllowedMultichainChains(
  chains: Chain[],
  options: MultichainEligibilityOptions,
): Chain[] {
  return chains.filter((chain) => isAllowedMultichainChain(chain, options));
}

export function matchesMultichainType(
  caip19: string,
  chainType: MultichainChainType,
): boolean {
  switch (chainType) {
    case "evm":
      return caip19.startsWith("eip155:");
    case "solana":
      return caip19.startsWith("solana:");
    case "bitcoin":
      return caip19.startsWith("bip122:");
    case "tron":
      return caip19.startsWith("tron:");
  }
}

export function findSupportedChainsForCurrency(
  currency: Currency,
  chains: Chain[],
  chainType?: MultichainChainType,
): Chain[] {
  return chains.filter((chain) => {
    try {
      const caip19 = currency.on(chain);
      return chainType ? matchesMultichainType(caip19, chainType) : true;
    } catch {
      return false;
    }
  });
}

export function findFirstSupportedChainSelection(
  currency: Currency,
  chains: Chain[],
):
  | {
      chain: Chain;
      chainType: MultichainChainType;
    }
  | undefined {
  for (const chain of chains) {
    try {
      const caip19 = currency.on(chain);
      if (caip19.startsWith("eip155:")) {
        return { chain, chainType: "evm" };
      }
      if (caip19.startsWith("solana:")) {
        return { chain, chainType: "solana" };
      }
      if (caip19.startsWith("bip122:")) {
        return { chain, chainType: "bitcoin" };
      }
      if (caip19.startsWith("tron:")) {
        return { chain, chainType: "tron" };
      }
    } catch {
      continue;
    }
  }

  return undefined;
}
